import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import Layout from "../components/app/Layout";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Check, X, Sparkles, Landmark, Activity } from "lucide-react";

const TABS = [
    { id: "pending", label: "Pending Wineries" },
    { id: "all", label: "All Wineries" },
    { id: "quotes", label: "Quote Requests" },
];

export default function AdminDashboard() {
    const { user, loading } = useAuth();
    const [tab, setTab] = useState("pending");
    const [pending, setPending] = useState([]);
    const [all, setAll] = useState([]);
    const [quotes, setQuotes] = useState([]);
    const [stats, setStats] = useState(null);

    const reload = async () => {
        const [p, a, q, s] = await Promise.all([
            api.get("/admin/wineries?status=pending"),
            api.get("/admin/wineries"),
            api.get("/admin/quote-requests"),
            api.get("/admin/stats"),
        ]);
        setPending(p.data); setAll(a.data); setQuotes(q.data); setStats(s.data);
    };

    useEffect(() => { if (user?.role === "admin") reload(); }, [user]);

    if (loading) return <Layout><div className="px-6 py-32 text-[#71717A]">Loading...</div></Layout>;
    if (!user) return <Navigate to="/login?redirect=/admin" replace />;
    if (user.role !== "admin") return <Navigate to="/" replace />;

    const act = async (path, msg) => {
        try { await api.post(path); toast.success(msg); reload(); }
        catch (e) { toast.error(formatError(e)); }
    };

    return (
        <Layout>
            <div className="px-6 md:px-12 lg:px-16 pt-10 pb-32">
                <p className="overline">Administration</p>
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                    <h1 className="font-display text-5xl md:text-6xl mt-3">Control Room</h1>
                    <div className="flex flex-wrap gap-3">
                        <a href="/admin/ops" className="btn-outline !py-2 !px-4 !text-xs" data-testid="admin-link-ops">
                            <Activity className="w-3.5 h-3.5" /> Ops Supervisor
                        </a>
                        <a href="/admin/finance/bank" className="btn-outline !py-2 !px-4 !text-xs" data-testid="admin-link-bank">
                            <Landmark className="w-3.5 h-3.5" /> Bank Configuration
                        </a>
                        <a href="/admin/finance/invoices" className="btn-outline !py-2 !px-4 !text-xs" data-testid="admin-link-invoices">
                            <Landmark className="w-3.5 h-3.5" /> Invoices
                        </a>
                    </div>
                </div>

                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12" data-testid="admin-stats">
                        <Stat label="Wineries" value={stats.wineries_total} />
                        <Stat label="Pending" value={stats.wineries_pending} accent />
                        <Stat label="Wines Listed" value={stats.wines_total} />
                        <Stat label="New Requests" value={stats.quote_requests_new} accent />
                    </div>
                )}

                <div className="flex gap-2 mt-16 border-b border-[#27272A]" data-testid="admin-tabs">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`admin-tab-${t.id}`}
                            className={`px-6 py-4 text-sm tracking-[0.15em] uppercase relative ${tab === t.id ? "text-[#C9A961]" : "text-[#71717A] hover:text-white"}`}
                        >
                            {t.label}
                            {tab === t.id && <span className="absolute bottom-0 left-0 right-0 h-px bg-[#C9A961]" />}
                        </button>
                    ))}
                </div>

                <div className="mt-12">
                    {tab === "pending" && (
                        <WineryTable
                            wineries={pending}
                            testid="pending-wineries-table"
                            actions={(w) => (
                                <>
                                    <button onClick={() => act(`/admin/wineries/${w.id}/approve`, "Winery approved.")} className="btn-gold !py-2 !px-4 mr-2" data-testid={`approve-${w.id}`}><Check className="w-4 h-4" /> Approve</button>
                                    <button onClick={() => act(`/admin/wineries/${w.id}/reject`, "Winery rejected.")} className="btn-outline !py-2 !px-4 !text-white !border-[#27272A] hover:!bg-red-500 hover:!border-red-500 hover:!text-black" data-testid={`reject-${w.id}`}><X className="w-4 h-4" /> Reject</button>
                                </>
                            )}
                        />
                    )}
                    {tab === "all" && (
                        <WineryTable
                            wineries={all}
                            testid="all-wineries-table"
                            actions={(w) => (
                                w.status !== "premium" ? (
                                    <button onClick={() => act(`/admin/wineries/${w.id}/premium`, "Promoted to premium.")} className="btn-outline !py-2 !px-4" data-testid={`premium-${w.id}`}><Sparkles className="w-4 h-4" /> Premium</button>
                                ) : <span className="text-[#C9A961] text-xs uppercase tracking-wide">Premium</span>
                            )}
                        />
                    )}
                    {tab === "quotes" && (
                        <div className="border border-[#27272A]" data-testid="admin-quotes-table">
                            {quotes.length === 0 ? <p className="p-16 text-center text-[#A1A1AA] font-display text-2xl">No requests yet.</p> : (
                                <table className="w-full text-sm">
                                    <thead className="bg-[#0A0A0A]">
                                        <tr className="text-left text-[#71717A] uppercase tracking-wide text-xs">
                                            <th className="py-4 px-4">Date</th>
                                            <th className="py-4 px-4">Importer</th>
                                            <th className="py-4 px-4">Country</th>
                                            <th className="py-4 px-4">Winery</th>
                                            <th className="py-4 px-4">Wine</th>
                                            <th className="py-4 px-4">Cases</th>
                                            <th className="py-4 px-4">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {quotes.map((q) => (
                                            <tr key={q.id} className="border-t border-[#27272A]" data-testid={`admin-quote-row-${q.id}`}>
                                                <td className="py-4 px-4 text-[#A1A1AA]">{new Date(q.created_at).toLocaleDateString()}</td>
                                                <td className="py-4 px-4">{q.importer_name}<br /><span className="text-xs text-[#71717A]">{q.importer_company}</span></td>
                                                <td className="py-4 px-4">{q.country}</td>
                                                <td className="py-4 px-4">{q.winery_name}</td>
                                                <td className="py-4 px-4 font-display text-lg">{q.wine_name}</td>
                                                <td className="py-4 px-4">{q.quantity}</td>
                                                <td className="py-4 px-4 text-xs uppercase tracking-wide" style={{ color: q.status === "new" ? "#C9A961" : "#71717A" }}>{q.status}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}

function Stat({ label, value, accent }) {
    return (
        <div className={`border ${accent ? "border-[#C9A961]" : "border-[#27272A]"} p-6`}>
            <p className="text-xs text-[#71717A] uppercase tracking-wide">{label}</p>
            <p className={`font-display text-5xl mt-2 ${accent ? "text-[#C9A961]" : "text-white"}`}>{value}</p>
        </div>
    );
}

function WineryTable({ wineries, actions, testid }) {
    if (wineries.length === 0) return <div className="border border-[#27272A] p-16 text-center"><p className="font-display text-2xl text-[#A1A1AA]">Nothing here.</p></div>;
    return (
        <div className="border border-[#27272A]" data-testid={testid}>
            <table className="w-full text-sm">
                <thead className="bg-[#0A0A0A]">
                    <tr className="text-left text-[#71717A] uppercase tracking-wide text-xs">
                        <th className="py-4 px-4">Winery</th>
                        <th className="py-4 px-4">Region</th>
                        <th className="py-4 px-4">Email</th>
                        <th className="py-4 px-4">Submitted</th>
                        <th className="py-4 px-4">Status</th>
                        <th className="py-4 px-4 text-right">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {wineries.map((w) => (
                        <tr key={w.id} className="border-t border-[#27272A]" data-testid={`admin-winery-row-${w.id}`}>
                            <td className="py-4 px-4 font-display text-lg">{w.name}</td>
                            <td className="py-4 px-4">{w.region}</td>
                            <td className="py-4 px-4 text-[#A1A1AA]">{w.email}</td>
                            <td className="py-4 px-4 text-[#71717A]">{new Date(w.created_at).toLocaleDateString()}</td>
                            <td className="py-4 px-4 text-xs uppercase tracking-wide" style={{ color: w.status === "approved" || w.status === "premium" ? "#C9A961" : w.status === "rejected" ? "#EF4444" : "#FBBF24" }}>{w.status}</td>
                            <td className="py-4 px-4 text-right whitespace-nowrap">{actions(w)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
